/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useContext, useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { InboundContext } from '../../../context/InboundContext';
import { Logo } from '../../common';
import { clearStorage } from '../../../utils/auth-utils';
import PoweredbySaral from '../../../assets/images/powered-by-saral2.svg';
import styles from './Sidebar.module.css';
import 'react-tooltip/dist/react-tooltip.css';
import CollapseIcon from '../../../assets/icons/CollapseIcon';
import { sidebarLinks as links, ugcLinks } from '../../../utils/constants';

type SidebarProps = {
  showSidebar: boolean;
  setShowSidebar: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function Sidebar({ showSidebar, setShowSidebar }: SidebarProps) {
  const { inboundData, isNonAffiliate } = useContext(InboundContext);
  const sidebarRef = useRef<null | HTMLDivElement>(null);
  const [isExpanded, setExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  const handleLogout = () => {
    clearStorage();
    toast.success('Logged out Successfully!');
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  useEffect(() => {
    const handleResize = () => {
      const isMobileView = window.innerWidth <= 768; // Adjust breakpoint as needed
      setIsMobile(isMobileView);
      if (isMobileView) {
        setShowSidebar(false);
        setExpanded(true);
      } else {
        setShowSidebar(true);
        setExpanded(false);
      }
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setShowSidebar]);

  useEffect(() => {
    if (showSidebar && sidebarRef.current) {
      sidebarRef.current.style.transform = 'translate(0)';
    } else if (!showSidebar && sidebarRef.current && isMobile) {
      sidebarRef.current.style.transform = 'translate(-130%)';
    }
  }, [showSidebar, isMobile]);

  const sidebarLinks = isNonAffiliate ? ugcLinks : links;

  return (
    <>
      {showSidebar && (
        <button
          type="button"
          className={styles.sidebarOverlay}
          onClick={() => setShowSidebar(false)}
          aria-label="Close sidebar"
        />
      )}
      <div className={`${styles.sidebarContainer} ${!isExpanded ? styles.sidebarCollapsed : ''}`} ref={sidebarRef}>
        <div>
          {inboundData?.landingPage?.images?.logo ? (
            <Logo isExpanded={isExpanded} logoUrl={inboundData?.landingPage?.images?.logo} />
          ) : (
            <Logo isExpanded={isExpanded} logoUrl="https://saral-docs-prod.s3.amazonaws.com/Saral+White+Logo.png" />
          )}
        </div>
        <div className={styles.linksContainer}>
          {sidebarLinks.map((link, index) => {
            if (link.disabled) {
              return (
                <div
                  key={index}
                  className={styles.disabedLink}
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Coming Soon!"
                  data-tooltip-place="top"
                  style={{ textAlign: !isExpanded ? 'center' : 'left' }}
                >
                  {link.logo}
                  <span className={!isExpanded ? styles.collapsedNavItemLabel : ''} style={{ marginLeft: '1ch' }}>
                    {link.label}
                  </span>
                  <Tooltip id="tooltip" style={{ opacity: 1, backgroundColor: '#000', color: '#fff' }} />
                </div>
              );
            }

            return (
              <NavLink
                end
                className={({ isActive }) => {
                  let c = isActive ? styles.activeLink : '';
                  c += !isExpanded ? ` ${styles.collapsedNavAnchor}` : '';
                  return c;
                }}
                key={index}
                to={link.href}
              >
                {link.logo}
                <span style={{ marginLeft: '1ch' }} className={!isExpanded ? styles.collapsedNavItemLabel : ''}>
                  {link.label}
                </span>
              </NavLink>
            );
          })}
          <button
            type="button"
            className={styles.logoutBtn}
            onClick={handleLogout}
            style={{ textAlign: !isExpanded ? 'center' : 'left' }}
          >
            🚪
            <span style={{ marginLeft: '1ch' }} className={!isExpanded ? styles.collapsedNavItemLabel : ''}>
              Log Out
            </span>
          </button>
        </div>
        <div
          className={styles.collapseContainer}
          style={{ justifyContent: isExpanded ? 'flex-start' : 'center' }}
          onClick={() => setExpanded((b) => !b)}
          aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          <CollapseIcon style={{ transform: !isExpanded ? 'rotate(180deg)' : '' }} /> {isExpanded ? 'Collapse' : ''}
        </div>
        {isExpanded && (
          <a href="https://getsaral.com" target="_blank" rel="noreferrer">
            <img
              src={PoweredbySaral}
              alt="Powered by Saral"
              className={`${styles.poweredBySaral} ${!isExpanded ? styles.poweredBySaralCollapsed : ''}`}
            />
          </a>
        )}
      </div>
    </>
  );
}
